<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  type ListaItens,
  type SeletorItem,
} from '@Lar21/imaginii-web-components/vue'

import {
  encontrarOpcaoDoCatalogo,
  preservarEstadoSemSelecaoVazia,
  prepararEstadoSomenteLeitura,
} from '@next/compartilhados/catalogos/apresentacao/estado-seletor-catalogo'

import {
  CHAVE_I18N_CATALOGO_EMPREGADOR,
  criarEstadoSeletorCatalogoEmpregador,
  criarPropriedadesPontotelSeletorEmpregador,
  extrairValorDoSeletorCatalogoEmpregador,
  filtrarOpcoesEmpregador,
} from './adaptador-catalogo-empregador'
import { usarCatalogoEmpregador } from './usar-catalogo-empregador'
import type {
  FiltrosCatalogoEmpregador,
  OpcaoEmpregador,
  SelecaoCatalogoEmpregador,
} from '../catalogo-empregador.tipos'

type Props = Readonly<{
  modelValue: SelecaoCatalogoEmpregador
  selecaoMultipla?: boolean
  tamanho?: 'P' | 'M' | 'G'
  predicado?: (empregador: OpcaoEmpregador) => boolean
  filtros?: FiltrosCatalogoEmpregador
  desabilitado?: boolean
  somenteLeitura?: boolean
  permiteSelecionarTodos?: boolean
  permiteLimpar?: boolean
  habilitarDuploClique?: boolean
  label?: string
  placeholder?: string
  mensagemErro?: string | null
  info?: string | null
}>

const props = withDefaults(defineProps<Props>(), {
  selecaoMultipla: false,
  tamanho: 'M',
  desabilitado: false,
  somenteLeitura: false,
  permiteSelecionarTodos: true,
  permiteLimpar: true,
  habilitarDuploClique: false,
  mensagemErro: null,
  info: null,
})

const emit = defineEmits<{
  'update:modelValue': [valor: SelecaoCatalogoEmpregador]
  'filtrando-itens': [filtro: string]
  'duplo-clique': [opcao: OpcaoEmpregador]
  'update:selecionouTodos': [selecionouTodos: boolean]
}>()

const { t } = useI18n()
const catalogo = usarCatalogoEmpregador(computed(() => !props.somenteLeitura))

const opcoesFiltradas = computed(() => filtrarOpcoesEmpregador(
  catalogo.itens.value,
  props.filtros,
  props.predicado,
))

const estadoSeletor = computed(() => prepararEstadoSomenteLeitura(
  criarEstadoSeletorCatalogoEmpregador(
    opcoesFiltradas.value,
    props.modelValue,
    props.selecaoMultipla,
  ),
  props.somenteLeitura,
  props.modelValue,
))

const erroExibido = computed(() => catalogo.erro.value
  ? t(`${CHAVE_I18N_CATALOGO_EMPREGADOR}.erroBusca`)
  : props.mensagemErro)

const propriedadesSeletor = computed(() => (
  criarPropriedadesPontotelSeletorEmpregador({
    traduzir: t,
    ...(props.label !== undefined ? { label: props.label } : {}),
    ...(props.placeholder !== undefined
      ? { placeholder: props.placeholder }
      : {}),
    erro: erroExibido.value,
  })
))

const alterarSelecao = (estado: ListaItens): void => {
  emit(
    'update:modelValue',
    extrairValorDoSeletorCatalogoEmpregador(
      preservarEstadoSemSelecaoVazia(
        estado,
        estadoSeletor.value,
        props.permiteLimpar,
      ),
      opcoesFiltradas.value,
      props.selecaoMultipla,
    ),
  )
}

const tratarDuploClique = (item: SeletorItem): void => {
  const opcao = encontrarOpcaoDoCatalogo(opcoesFiltradas.value, item)
  if (opcao) emit('duplo-clique', opcao)
}

</script>

<template>
  <PontotelSeletor
    v-bind="propriedadesSeletor"
    :model-value="estadoSeletor"
    :tamanho="tamanho"
    :carregar-itens="null"
    :selecao-multipla="selecaoMultipla"
    :desabilitado="desabilitado"
    :modo-exibicao="somenteLeitura"
    :permite-selecionar-todos="permiteSelecionarTodos"
    :habilitar-duplo-clique="habilitarDuploClique"
    :info="info"
    :recarregar-todos="!somenteLeitura"
    :recarregando-todos="catalogo.atualizando.value"
    @update:model-value="alterarSelecao"
    @filtrando-itens="emit('filtrando-itens', $event)"
    @duplo-clique="tratarDuploClique"
    @update:selecionou-todos="emit('update:selecionouTodos', $event)"
    @recarregar-todos="catalogo.atualizar"
  >
    <template
      v-if="$slots.icone"
      #icone
    >
      <slot name="icone" />
    </template>
  </PontotelSeletor>
</template>
